(() => {
  /* =======================
     INIT
  ======================= */
  let root = document.querySelector("[data-collection-section]");
  if (!root) return;

  let sectionId = root.dataset.sectionId;
  let abortController = null;
  let debounceTimer = null;
  let hasScrolledOnFilter = false;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  /* =======================
     ANTI CLS (1x au load)
  ======================= */
  window.addEventListener("load", () => {
    const dynamic = document.querySelector("[data-collection-dynamic]");
    if (!dynamic) return;

    dynamic.style.setProperty(
      "--collection-min-height",
      `${dynamic.offsetHeight}px`,
    );
  });

  /* =======================
     HELPERS
  ======================= */
  const serializeForm = (form) => {
    const fd = new FormData(form);
    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) {
      if (v !== "" && v != null) params.append(k, v);
    }
    return params.toString();
  };

  const extractCountFromDoc = (doc) => {
    const el = doc.querySelector("[data-results-count-value]");
    if (!el) return null;
    const n = parseInt(el.textContent.replace(/[^\d]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  };

  const updateResultsButtonCount = (count) => {
    const btn = document.querySelector("[data-results-button]");
    if (!btn) return;
    const span = btn.querySelector("[data-results-count]");
    if (!span) return;
    span.textContent = count;
  };

  const scrollToCollectionTop = () => {
    const el = document.querySelector("[data-collection-section]");
    if (!el) return;

    const offset = 80;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  /* =======================
     FLIP (HORIZON STYLE)
  ======================= */
  const capturePositions = () => {
    const map = new Map();
    document
      .querySelectorAll("[data-collection-dynamic] [data-product-id]")
      .forEach((el) => {
        map.set(el.dataset.productId, el.getBoundingClientRect());
      });
    return map;
  };

  const animateFLIP = (oldPositions) => {
    if (prefersReducedMotion || !oldPositions?.size) return;

    document
      .querySelectorAll("[data-collection-dynamic] [data-product-id]")
      .forEach((el) => {
        const id = el.dataset.productId;
        if (!oldPositions.has(id)) return;

        const oldRect = oldPositions.get(id);
        const newRect = el.getBoundingClientRect();

        const dx = oldRect.left - newRect.left;
        const dy = oldRect.top - newRect.top;

        if (dx === 0 && dy === 0) return;

        el.style.transform = `translate(${dx}px, ${dy}px)`;
        el.style.transition = "transform 0s";

        requestAnimationFrame(() => {
          el.style.transition = "transform 360ms cubic-bezier(0.4, 0, 0.2, 1)";
          el.style.transform = "translate(0, 0)";
        });
      });
  };

  const fadeInNewProducts = (oldPositions) => {
    if (prefersReducedMotion) return;

    document
      .querySelectorAll("[data-collection-dynamic] [data-product-id]")
      .forEach((el) => {
        if (oldPositions.has(el.dataset.productId)) return;
        el.style.opacity = "0";
        el.style.transition = "opacity 200ms ease";
        requestAnimationFrame(() => {
          el.style.opacity = "1";
        });
      });
  };

  /* =======================
     DOM UPDATES
  ======================= */
  const replaceDynamicContent = (doc) => {
    const newDynamic = doc.querySelector("[data-collection-dynamic]");
    const currentDynamic = document.querySelector("[data-collection-dynamic]");
    if (!newDynamic || !currentDynamic) return false;
    currentDynamic.replaceWith(newDynamic);
    return true;
  };

  const updateFiltersUI = (doc) => {
    const newFilters = doc.querySelector("[data-collection-filters]");
    const currentFilters = document.querySelector("[data-collection-filters]");
    if (newFilters && currentFilters) {
      currentFilters.replaceWith(newFilters);
    }
  };

  /* =======================
     BINDINGS
  ======================= */
  const bindResultsButton = () => {
    const btn = document.querySelector("[data-results-button]");
    if (!btn || btn.dataset.bound === "1") return;
    btn.dataset.bound = "1";

    btn.addEventListener("click", () => {
      const modal = btn.closest("modal-dialog");
      if (modal && typeof modal.hide === "function") modal.hide();
    });
  };

  const bindForms = () => {
    document
      .querySelectorAll('form[action*="/collections/"]')
      .forEach((form) => {
        if (form.dataset.bound === "1") return;
        form.dataset.bound = "1";

        const trigger = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            if (!hasScrolledOnFilter) {
              scrollToCollectionTop();
              hasScrolledOnFilter = true;
            }

            const oldPositions = capturePositions();
            const qs = serializeForm(form);
            fetchSection(`${form.action}?${qs}`, true, oldPositions);
          }, 120);
        };

        form.addEventListener("change", trigger);
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          trigger();
        });
        form.addEventListener("input", (e) => {
          if (e.target.matches("[data-price-min],[data-price-max]")) trigger();
        });
      });
  };

  const bindFilterPills = () => {
    const container = document.querySelector("[data-collection-dynamic]");
    if (!container) return;

    container.querySelectorAll("a[data-filter-pill]").forEach((pill) => {
      if (pill.dataset.bound === "1") return;
      pill.dataset.bound = "1";

      pill.addEventListener("click", (e) => {
        e.preventDefault();

        if (!hasScrolledOnFilter) {
          scrollToCollectionTop();
          hasScrolledOnFilter = true;
        }

        const oldPositions = capturePositions();
        fetchSection(pill.href, true, oldPositions);
      });
    });
  };

  /* =======================
     FETCH
  ======================= */
  const fetchSection = async (url, pushState, oldPositions) => {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const u = new URL(url, window.location.origin);
    u.searchParams.set("section_id", sectionId);

    try {
      const res = await fetch(u.toString(), {
        signal: abortController.signal,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const count = extractCountFromDoc(doc);
      if (count !== null) updateResultsButtonCount(count);

      replaceDynamicContent(doc);
      updateFiltersUI(doc);

      animateFLIP(oldPositions);
      fadeInNewProducts(oldPositions);

      root = document.querySelector("[data-collection-section]");
      sectionId = root?.dataset.sectionId || sectionId;

      bindForms();
      bindResultsButton();
      bindFilterPills();

      if (!window.location.search.includes("filter.")) {
        hasScrolledOnFilter = false;
      }

      if (pushState) {
        const clean = new URL(url, window.location.origin);
        window.history.pushState(
          { url: clean.toString() },
          "",
          clean.toString(),
        );
      }
    } catch (e) {
      if (e.name !== "AbortError") console.error(e);
    }
  };

  /* =======================
     START
  ======================= */
  bindForms();
  bindResultsButton();
  bindFilterPills();

  window.addEventListener("popstate", (e) => {
    const oldPositions = capturePositions();
    fetchSection(e.state?.url || window.location.href, false, oldPositions);
  });
})();
